import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

// Token management
function getStoredTokens() {
  return {
    access: localStorage.getItem('access_token'),
    refresh: localStorage.getItem('refresh_token'),
  };
}

function storeTokens(access, refresh) {
  if (access) localStorage.setItem('access_token', access);
  if (refresh) localStorage.setItem('refresh_token', refresh);
}

function clearTokens() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
}

// Axios instance with auth header
const authAxios = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

// Add token to every request
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.withCredentials = true;
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const { access } = getStoredTokens();
    if (!access) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const response = await authAxios.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      // Try refresh
      try {
        const { refresh } = getStoredTokens();
        if (refresh) {
          const refreshRes = await axios.post(`${API_URL}/api/auth/refresh`, 
            { refresh_token: refresh },
            { withCredentials: true }
          );
          if (refreshRes.data.access_token) {
            storeTokens(refreshRes.data.access_token, null);
            const retryRes = await authAxios.get('/auth/me');
            setUser(retryRes.data);
            return;
          }
        }
        clearTokens();
        setUser(false);
      } catch {
        clearTokens();
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, 
      { email, password },
      { withCredentials: true }
    );
    const data = response.data;
    if (data.access_token) {
      storeTokens(data.access_token, data.refresh_token);
    }
    setUser(data);
    return data;
  };

  const register = async (email, password, name, districtName) => {
    const response = await axios.post(`${API_URL}/api/auth/register`,
      { email, password, name, district_name: districtName },
      { withCredentials: true }
    );
    const data = response.data;
    if (data.access_token) {
      storeTokens(data.access_token, data.refresh_token);
    }
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await authAxios.post('/auth/logout');
    } finally {
      clearTokens();
      setUser(false);
    }
  };

  const refreshToken = async () => {
    try {
      const { refresh } = getStoredTokens();
      const res = await axios.post(`${API_URL}/api/auth/refresh`, 
        { refresh_token: refresh },
        { withCredentials: true }
      );
      if (res.data.access_token) {
        storeTokens(res.data.access_token, null);
      }
      await checkAuth();
    } catch (error) {
      clearTokens();
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      refreshToken,
      isAuthenticated: user !== null && user !== false 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
